import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { FlaskConical, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Activity, Loader2, Dna } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const LabInterpretation = ({ onLabAnalysisComplete, patientData }) => {
  const [activeTab, setActiveTab] = useState('blood'); // 'blood' | 'genetics'
  const [labText, setLabText] = useState('');
  const [geneticsText, setGeneticsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [labAnalysis, setLabAnalysis] = useState(null);
  const [geneticsAnalysis, setGeneticsAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');

  const interpretLabs = async () => {
    if (!labText.trim()) {
      setError('Please enter lab results to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    
    // Progress messages
    setLoadingMessage('Extracting lab values from your results...');
    setTimeout(() => setLoadingMessage('Analyzing lab markers and identifying abnormal values...'), 3000);
    setTimeout(() => setLoadingMessage('Generating clinical interpretation with Dr. Peptide AI...'), 8000);
    setTimeout(() => setLoadingMessage('Creating personalized peptide recommendations...'), 15000);

    try {
      const response = await axios.post(`${API}/labs/interpret`, {
        text: labText,
        test_type: 'blood',
        patient_info: patientData || {}
      }, {
        timeout: 60000 // 60 second timeout
      });

      if (response.data.success) {
        setLabAnalysis(response.data);
        setLoadingMessage('Analysis complete!');
        // Notify parent component that labs have been analyzed
        if (onLabAnalysisComplete) {
          onLabAnalysisComplete({
            ...response.data,
            test_type: 'blood'
          });
        }
      } else {
        setError(response.data.error || 'Failed to analyze labs');
      }
    } catch (err) {
      console.error('Lab interpretation error:', err);
      setError('Failed to interpret labs. Please try again.');
      setLoadingMessage('');
    } finally {
      setLoading(false);
      setTimeout(() => setLoadingMessage(''), 2000);
    }
  };

  const interpretGenetics = async () => {
    if (!geneticsText.trim()) {
      setError('Please enter genetics test results to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    
    // Progress messages for genetics
    setLoadingMessage('Extracting genetic markers from your data...');
    setTimeout(() => setLoadingMessage('Identifying clinically relevant SNPs...'), 3000);
    setTimeout(() => setLoadingMessage('Analyzing genetic variants with Dr. Peptide AI...'), 8000);
    setTimeout(() => setLoadingMessage('Generating personalized peptide recommendations based on your genetics...'), 15000);

    try {
      const response = await axios.post(`${API}/genetics/interpret`, {
        text: geneticsText,
        test_type: 'genetics',
        patient_info: patientData || {}
      }, {
        timeout: 60000 // 60 second timeout
      });

      if (response.data.success) {
        setGeneticsAnalysis(response.data);
        setLoadingMessage('Genetics analysis complete!');
        // Notify parent component that genetics have been analyzed
        if (onLabAnalysisComplete) {
          onLabAnalysisComplete({
            ...response.data,
            test_type: 'genetics'
          });
        }
      } else {
        setError(response.data.error || 'Failed to analyze genetics');
      }
    } catch (err) {
      console.error('Genetics interpretation error:', err);
      setError('Failed to interpret genetics test. Please try again.');
      setLoadingMessage('');
    } finally {
      setLoading(false);
      setTimeout(() => setLoadingMessage(''), 2000);
    }
  };

  const getLabStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'critical_high':
      case 'critical_low':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'high':
      case 'low':
        return <TrendingUp className="h-4 w-4 text-yellow-600" />;
      case 'normal':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getLabStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'critical_high':
      case 'critical_low':
        return 'destructive';
      case 'high':
      case 'low':
        return 'warning';
      case 'normal':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-6 w-6" />
            Lab & Genetics Interpretation AI
          </CardTitle>
          <CardDescription>
            Upload your blood test results or genetics data. Dr. Peptide will analyze them and provide personalized peptide recommendations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="blood">
                <FlaskConical className="h-4 w-4 mr-2" />
                Blood Tests
              </TabsTrigger>
              <TabsTrigger value="genetics">
                <Dna className="h-4 w-4 mr-2" />
                Genetics Tests
              </TabsTrigger>
            </TabsList>

            <TabsContent value="blood" className="space-y-4">
              <div>
                <Textarea
                  placeholder="Paste your blood test results here...\n\nExample:\nHemoglobin A1c: 5.7%\nFasting Glucose: 95 mg/dL\nTotal Cholesterol: 200 mg/dL\nHDL: 55 mg/dL\nLDL: 120 mg/dL\nTriglycerides: 125 mg/dL\nTSH: 2.5 mIU/L"
                  value={labText}
                  onChange={(e) => setLabText(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              <Button
                onClick={interpretLabs}
                disabled={loading || !labText.trim()}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing Blood Tests...
                  </>
                ) : (
                  <>
                    <FlaskConical className="h-4 w-4 mr-2" />
                    Interpret Blood Test Results
                  </>
                )}
              </Button>

              {loading && loadingMessage && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Activity className="h-4 w-4 text-blue-600 animate-pulse" />
                  <AlertDescription className="text-blue-900">
                    {loadingMessage}
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="genetics" className="space-y-4">
              <div>
                <Textarea
                  placeholder="Paste your genetics test results here...\n\nExample (23andMe, AncestryDNA, etc.):\nrs1801133 (MTHFR): A/G\nrs1801131 (MTHFR): A/C\nrs4680 (COMT): G/A\nrs662799 (APOA5): T/C\nrs5219 (KCNJ11): C/T\n\nOr paste raw genetic data, SNP reports, or analysis from genetic testing companies."
                  value={geneticsText}
                  onChange={(e) => setGeneticsText(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              <Button
                onClick={interpretGenetics}
                disabled={loading || !geneticsText.trim()}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing Genetics...
                  </>
                ) : (
                  <>
                    <Dna className="h-4 w-4 mr-2" />
                    Interpret Genetics Test Results
                  </>
                )}
              </Button>

              {loading && loadingMessage && (
                <Alert className="border-purple-200 bg-purple-50">
                  <Dna className="h-4 w-4 text-purple-600 animate-pulse" />
                  <AlertDescription className="text-purple-900">
                    {loadingMessage}
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {labAnalysis && (
        <>
          {/* Extracted Lab Values */}
          <Card>
            <CardHeader>
              <CardTitle>Extracted Lab Values</CardTitle>
              <CardDescription>
                {labAnalysis.extracted_data?.labs?.length || 0} lab markers identified
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {labAnalysis.extracted_data?.labs?.map((lab, index) => (
                  <div key={index} className="flex items-start justify-between p-3 border rounded-lg">
                    <div className="flex items-start gap-3 flex-1">
                      {getLabStatusIcon(lab.status)}
                      <div className="flex-1">
                        <div className="font-medium">{lab.name}</div>
                        <div className="text-sm text-gray-600">
                          Value: <span className="font-semibold">{lab.value} {lab.unit}</span>
                        </div>
                        {lab.reference_range && (
                          <div className="text-xs text-gray-500">
                            Normal Range: {lab.reference_range}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge variant={getLabStatusColor(lab.status)}>
                      {lab.status || 'Unknown'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Clinical Interpretation */}
          {labAnalysis.ai_interpretation && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Dr. Peptide's Clinical Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700">
                    {labAnalysis.ai_interpretation}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Analysis Summary */}
          {labAnalysis.analysis && (
            <Card>
              <CardHeader>
                <CardTitle>Clinical Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Critical Values */}
                  {labAnalysis.analysis.critical_values?.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-semibold mb-2">Critical Values Detected:</div>
                        <ul className="list-disc pl-5 space-y-1">
                          {labAnalysis.analysis.critical_values.map((critical, i) => (
                            <li key={i}>{critical.name}: {critical.value} {critical.unit}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Abnormal Values */}
                  {labAnalysis.analysis.abnormal_values?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Values Requiring Attention:</h4>
                      <div className="grid gap-2">
                        {labAnalysis.analysis.abnormal_values.map((abnormal, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            {getLabStatusIcon(abnormal.status)}
                            <span>{abnormal.name}: {abnormal.value} {abnormal.unit}</span>
                            {abnormal.interpretation && (
                              <span className="text-gray-600">({abnormal.interpretation})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {labAnalysis.analysis.recommendations?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Recommendations:</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                        {labAnalysis.analysis.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Lab analysis complete! These results will be used to personalize your peptide protocol.
            </AlertDescription>
          </Alert>
        </>
      )}

      {/* Genetics Analysis Results */}
      {geneticsAnalysis && (
        <>
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Dna className="h-5 w-5 text-purple-600" />
                Genetics Analysis Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Key Genetic Findings */}
                {geneticsAnalysis.key_findings && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Key Genetic Findings:</h4>
                    <div className="grid gap-2">
                      {geneticsAnalysis.key_findings.map((finding, i) => (
                        <div key={i} className="p-3 border rounded-lg bg-white">
                          <div className="font-medium text-sm">{finding.gene || finding.snp}</div>
                          <div className="text-xs text-gray-600 mt-1">{finding.description}</div>
                          {finding.impact && (
                            <Badge variant={finding.impact === 'high' ? 'destructive' : 'secondary'} className="mt-2">
                              {finding.impact} impact
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Interpretation */}
                {geneticsAnalysis.ai_interpretation && (
                  <div className="p-4 bg-white rounded-lg border border-purple-200">
                    <h4 className="font-semibold mb-2 text-purple-900">Dr. Peptide's Genetic Analysis:</h4>
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700">
                        {geneticsAnalysis.ai_interpretation}
                      </div>
                    </div>
                  </div>
                )}

                {/* Peptide Recommendations Based on Genetics */}
                {geneticsAnalysis.peptide_recommendations && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Peptide Recommendations Based on Your Genetics:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                      {geneticsAnalysis.peptide_recommendations.map((rec, i) => (
                        <li key={i}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Alert className="border-purple-200 bg-purple-50">
            <Dna className="h-4 w-4 text-purple-600" />
            <AlertDescription>
              Genetics analysis complete! Your genetic data will be used to personalize your peptide protocol and optimize recommendations.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
};

export default LabInterpretation;
